<script setup>
import { ref, computed, onMounted } from 'vue'
import {
  Dialog,
  DialogTitle,
  DialogPanel,
  TransitionChild,
  TransitionRoot
} from '@headlessui/vue'
import {
  Bars3Icon,
  TrashIcon,
  XMarkIcon,
  PencilSquareIcon,
  ExclamationTriangleIcon
} from '@heroicons/vue/24/outline'
import ModalComponent from '@/components/ModalComponent.vue'
import LanguageSwitcher from '@/components/LanguageSwitcher.vue'
import OpenSidebarButton from '@/components/OpenSidebarButton.vue'
import CloseSidebarButton from '@/components/CloseSidebarButton.vue'
import { useUsersStore } from '@/stores/users'
import { useMessagesStore } from '@/stores/messages'
import { RouterLink, useRouter, useRoute } from 'vue-router'
import assistantApi from '@/api/assistant'
import axios from '@/api/axiosInstance.js'
import { useModal } from '@/composables/modal.js'
import { useCaptcha } from '@/composables/useCaptcha';
import { useI18n } from "vue-i18n";
import { useCaptchaStore } from '@/stores/captcha'

const { t } = useI18n();
const usersStore = useUsersStore()
const messagesStore = useMessagesStore()
const router = useRouter()
const route = useRoute()
const { show: isModalVisible, showModal, hideModal } = useModal()
const captchaStore = useCaptchaStore()
const modalTitle = ref('')
const modalContent = ref('')
const modalOnDelete = ref(null)
const isDeleting = ref(false);

const captchaProvider = import.meta.env.VITE_APP_CAPTCHA_PROVIDER || 'recaptcha';
const isTurnstile = computed(() => captchaProvider === 'turnstile');

const { getCaptchaToken} = useCaptcha();

// --- START: Swipe to close sidebar ---
const touchStartX = ref(0);
const touchDeltaX = ref(0);
const handleTouchStart = (event) => {
  touchStartX.value = event.touches[0].clientX;
  touchDeltaX.value = 0;
};
const handleTouchMove = (event) => {
  touchDeltaX.value = event.touches[0].clientX - touchStartX.value;
};
const handleTouchEnd = () => {
  if (touchDeltaX.value < -50) {
    sidebarOpen.value = false;
  }
};
// --- END: Swipe to close sidebar ---

// --- START: Swipe to open sidebar ---
const canSwipeOpen = ref(false);

const handleOpenGestureStart = (event) => {
  if (sidebarOpen.value) return;

  touchStartX.value = event.touches[0].clientX;
  touchDeltaX.value = 0;
  canSwipeOpen.value = true;
};

const handleOpenGestureMove = (event) => {
  if (!canSwipeOpen.value) return;
  touchDeltaX.value = event.touches[0].clientX - touchStartX.value;
};

const handleOpenGestureEnd = () => {
  if (canSwipeOpen.value && touchDeltaX.value > 50) {
    sidebarOpen.value = true;
  }
  canSwipeOpen.value = false;
};
// --- END: Swipe to open sidebar ---


const navigation = computed(() => {
  const lang = route.params.lang || 'mne';
  const periods = {
    today: t('common.today'),
    yesterday: t('common.yesterday'),
    thisMonth: t('common.thisMonth'),
    previous: t('common.earlier')
  }

  const threadsByPeriod = Object.entries(periods)
    .map((period) => ({
      id: period[0],
      name: period[1],
      threads:
        usersStore.getCurrentUserThreads[period[0]]?.map((chat) => ({
          name: chat.name,
          href: `/${lang}/chats/${chat.thread_id}`,
          threadId: chat.thread_id,
          current:
            `/${lang}/chats/${chat.thread_id}` === router.currentRoute.value.path
        })) || []
    }))
    .filter((period) => period.threads.length > 0)

  return [
    {
      name: t('common.newConversation'),
      href: `/${lang}/chats/new`,
      icon: PencilSquareIcon,
      current: `/${lang}/chats/new` === router.currentRoute.value.path
    },
    ...threadsByPeriod
  ]
})

onMounted(async () => {
  await usersStore.setCurrentUserThreads()
})

const sidebarOpen = ref(false)

const handleDeleteAllChat = async () => {
  if (usersStore.getCurrentUserThreads.length === 0 || (
    usersStore.getCurrentUserThreads.today.length === 0 &&
    usersStore.getCurrentUserThreads.yesterday.length === 0 &&
    usersStore.getCurrentUserThreads.thisMonth.length === 0 &&
    usersStore.getCurrentUserThreads.previous.length === 0
  )) {
    return
  }

  modalTitle.value = t('deleteActions.deleteAll')
  modalContent.value = t('deleteActions.deleteAllWarning')
  modalOnDelete.value = async () => {
    hideModal() 

    try {
      localStorage.removeItem('uuid')
      const { data } = await axios.post('/user/create_uuid')
      localStorage.setItem('uuid', data.id)

      const userStore = useUsersStore()
      userStore.setCurrentUser({
        id: localStorage.getItem('uuid')
      })

      await userStore.setCurrentUserThreads()
      await router.push(`/${route.params.lang}/chats/new`)
      sidebarOpen.value = false; 
    } catch (error) {
      console.error('Error while deleting chats and resetting user:', error)
    }
  }
  showModal() 
}


const handleDeleteChat = async (periodId, threadId) => {
  const threads = usersStore.getCurrentUserThreads
  const threadName =
    threads[periodId].find((thread) => thread.thread_id === threadId)?.name ??
    ''
  modalTitle.value = t('deleteActions.deleteChatQuestion')
  modalContent.value = `${t('deleteActions.deleteChatExplanation')} "${threadName}".`
  isDeleting.value = true
    modalOnDelete.value = async () => {
    hideModal() 
    const token = await getCaptchaToken("deleteItem");
    assistantApi
      .deleteThread(threadId, token)
      .then(() => {
        const currentThreadId = router.currentRoute.value.params.threadId;
        usersStore.deleteThread(periodId, threadId);
        
        if (currentThreadId === threadId) {
          router.push(`/${route.params.lang}/chats/new`);
          sidebarOpen.value = false;
        }
      })
      .catch((error) => {
        console.log(error)
      })
  }
  showModal(); 
  isDeleting.value = false;
  if (isTurnstile.value) {
    captchaStore.resetToken();
    window.dispatchEvent(new Event('reset-turnstile'));
  }
}
</script>
<template>
  <ModalComponent>
    <div class="sm:flex sm:items-start">
      <div
        class="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10"
      >
        <ExclamationTriangleIcon
          class="h-6 w-6 text-red-500"
          aria-hidden="true"
        />
      </div>
      <div class="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
        <DialogTitle
          as="h3"
          class="text-base font-medium leading-6 text-gray-900"
        >{{ modalTitle }}
        </DialogTitle
        >
        <div class="mt-2">
          <p class="text-sm text-gray-500">{{ modalContent }}</p>
        </div>
      </div>
    </div>
    <div class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
      <button
        type="button"
        class="inline-flex w-full justify-center rounded-[20px] bg-red-500 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-400 sm:ml-3 sm:w-auto"
        :disabled="isDeleting"
        @click="modalOnDelete"
        @touchstart="modalOnDelete"
      >
        <span v-if="isDeleting">
            <svg aria-hidden="true" class="w-6 h-6 text-white animate-spin dark:text-gray-600 fill-[#00baf2]" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor"/>
              <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill"/>
            </svg>
            <span class="sr-only">Loading...</span>
        </span>
        <span v-else>
          {{ t('deleteActions.delete') }}
        </span>
      </button>
      <button
        ref="cancelButtonRef"
        type="button"
        class="mt-3 inline-flex w-full justify-center rounded-[20px] bg-white px-3 py-2 text-sm font-medium text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
        @click="hideModal"
        @touchstart="hideModal"
      >
        {{ t('deleteActions.cancel') }}
      </button>
    </div>
  </ModalComponent>
  <div
    @touchstart="handleOpenGestureStart"
    @touchmove="handleOpenGestureMove"
    @touchend="handleOpenGestureEnd"
  >
    <TransitionRoot as="template" :show="sidebarOpen">
      <Dialog
        as="div"
        class="relative z-50 lg:hidden"
      >
        <TransitionChild
          as="template"
          enter="transition-opacity ease-linear duration-300"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="transition-opacity ease-linear duration-300"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <div class="fixed inset-0 bg-gray-900/80" @click="sidebarOpen = false" />
        </TransitionChild>

        <div class="fixed inset-0 flex">
          <TransitionChild
            as="template"
            enter="transition ease-in-out duration-300 transform"
            enter-from="-translate-x-full"
            enter-to="translate-x-0"
            leave="transition ease-in-out duration-300 transform"
            leave-from="translate-x-0"
            leave-to="-translate-x-full"
          >
            <DialogPanel
              class="relative flex w-full flex-1"
              @touchstart="handleTouchStart"
              @touchmove="handleTouchMove"
              @touchend="handleTouchEnd"
              >
              <div class="flex grow flex-col overflow-y-auto bg-slate-100 px-6">
                <div class="relative flex h-16 shrink-0 items-center justify-center">
                  <p class="font-bold text-gray-darker">Supersteward</p>
                </div>
                <nav class="flex flex-1 flex-col">
                  <ul role="list" class="flex flex-1 flex-col gap-y-7">
                    <li>
                      <ul id="scrollbar" role="list" class="space-y-1">
                        <li
                          v-for="item in navigation.slice(0, 1)"
                          :key="item.name"
                          class="overflow-hidden text-ellipsis whitespace-nowrap"
                          @click="sidebarOpen = false"
                        >
                          <RouterLink
                            :to="item.href"
                            :disabled="true"
                            :class="[
                              item.name === t('common.newConversation')
                                ? 'bg-[#00baf2] text-white mt-4'
                                : 'text-gray-darker',
                              messagesStore.getMessageLoading
                                ? 'cursor-not-allowed'
                                : '',
                              'group flex gap-x-3 rounded-full p-3 text-sm font-medium leading-6',
                            ]"
                          >
                            <component
                              :is="item.icon"
                              class="h-6 w-6 shrink-0"
                              aria-hidden="true"
                            />
                            {{ item.name }}
                          </RouterLink>
                        </li>

                        <li
                          v-for="period in navigation.slice(1)"
                          :key="period.name"
                        >
                          <h3 class="px-3 py-1 text-sm font-bold">
                            {{ period.name }}
                          </h3>
                          <ul role="list" class="space-y-1">
                            <li
                              v-for="thread in period.threads"
                              :key="thread.threadId"
                            >
                              <RouterLink
                                :to="
                                  messagesStore.getMessageLoading
                                    ? ''
                                    : thread.href
                                "
                                :class="[
                                  thread.current ? 'bg-slate-200' : '',
                                  messagesStore.getMessageLoading
                                    ? 'cursor-not-allowed'
                                    : 'hover:bg-slate-200 active:bg-slate-200',
                                  'group flex items-center gap-x-3 rounded-full p-3 text-sm font-medium leading-6',
                                ]"
                                @click="sidebarOpen = false"
                              >
                                <component
                                  :is="thread.icon"
                                  v-if="thread.icon"
                                  class="h-6 w-6 shrink-0"
                                  aria-hidden="true"
                                />
                                <p
                                  class="inline-block w-full overflow-hidden text-ellipsis whitespace-nowrap"
                                  :title="thread.name"
                                >
                                  {{ thread.name }}
                                </p>
                                <TrashIcon
                                  v-if="thread.threadId"
                                  class="h-5 w-5 hover:text-red-500"
                                  @click.stop.prevent="
                                    messagesStore.getMessageLoading
                                      ? ''
                                      : handleDeleteChat(
                                          period.id,
                                          thread.threadId,
                                        )
                                  "
                                />
                              </RouterLink>
                            </li>
                          </ul>
                        </li>
                      </ul>
                    </li>
                  </ul>

                  <div
                    class="relative hover:cursor-pointer flex flex-row-reverse items-center justify-center
                           mt-2 text-white text-sm font-medium px-12 py-4 rounded-full bg-red-500
                            hover:bg-red-400
                           shadow-lg gap-x-3 transition-all duration-300 transform hover:scale-105 mb-3"
                    @click.stop.prevent="handleDeleteAllChat()"
                  >
                      <span class="">{{ t('deleteActions.deleteAll') }}</span>
                      <TrashIcon class="h-6 w-6" />
                  </div>
                </nav>
              </div>
              <CloseSidebarButton @click="sidebarOpen = false" />
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </TransitionRoot>

    <div
      class="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-80 lg:flex-col"
    >
      <div class="flex grow flex-col">
        <div
          class=" flex h-16 shrink-0 items-center justify-evenly border-b border-gray-light bg-[#f4f4f4] px-6"
        >
          <img
            class="h-14 w-auto"
            src="../assets/air_mne_horizontal_logo_mini@1x.png"
            srcset="
              ../assets/air_mne_horizontal_logo_mini@1x.png 1x,
              ../assets/air_mne_horizontal_logo_mini@1x.png 2x
            "
            alt="Air Montenegro"
          />
          <p class="font-bold text-gray-darker">Supersteward</p>
        </div>
        <nav
          class="masked-overflow flex h-[calc(100dvh-64px)] flex-1 flex-col border-r border-gray-light px-6 text-gray-darker lg:fixed lg:inset-y-16 lg:z-50 lg:flex"
        >
          <div class="flex justify-center py-4">
            <LanguageSwitcher />
          </div>
          <ul role="list" class="flex flex-1 flex-col gap-y-7">
            <li>
              <ul role="list" class="space-y-1">
                <li v-for="item in navigation.slice(0, 1)" :key="item.name">
                  <RouterLink
                    :to="messagesStore.getMessageLoading ? '' : item.href"
                    :class="[
                      item.current ? 'bg-[#00baf2] text-white' : '',
                      messagesStore.getMessageLoading
                        ? 'cursor-not-allowed'
                        : 'hover:bg-[#00baf2] hover:text-white',
                      item.name === t('common.newConversation') ? 'mt-4' : '',
                      'group flex gap-x-3 rounded-[20px] p-3 text-sm font-medium leading-6',
                    ]"
                  >
                    <component
                      :is="item.icon"
                      vif="item.icon"
                      class="h-6 w-6 shrink-0"
                      aria-hidden="true"
                    />
                    <p
                      class="inline-block w-[200px] overflow-hidden text-ellipsis whitespace-nowrap"
                      :title="item.name"
                    >
                      {{ item.name }}
                    </p>
                  </RouterLink>
                </li>

                <li v-for="period in navigation.slice(1)" :key="period.id">
                  <h3 class="px-3 py-1 text-sm font-bold">
                    {{ period.name }}
                  </h3>
                  <ul role="list" class="space-y-1">
                    <li v-for=" thread in period.threads" :key="thread.threadId">
                      <RouterLink
                        :to="messagesStore.getMessageLoading ? '' : thread.href"
                        :class="[
                          thread.current ? 'bg-gray-light' : '',
                          messagesStore.getMessageLoading
                            ? 'cursor-not-allowed'
                            : 'hover:bg-slate-200',
                          'group flex items-center gap-x-3 rounded-[20px] p-3 text-sm font-medium leading-6',
                        ]"
                      >
                        <component
                          :is="thread.icon"
                          v-if="thread.icon"
                          class="h-6 w-6 shrink-0"
                          aria-hidden="true"
                        />
                        <p
                          class="inline-block w-[200px] overflow-hidden text-ellipsis whitespace-nowrap"
                          :title="thread.name"
                        >
                          {{ thread.name }}
                        </p>
                        <TrashIcon
                          v-if="thread.threadId"
                          class="h-5 w-5 hover:text-red-500"
                          @click.stop.prevent="
                            messagesStore.getMessageLoading
                              ? ''
                              : handleDeleteChat(period.id, thread.threadId)
                          "
                        />
                      </RouterLink>
                    </li>
                  </ul>
                </li>
              </ul>
            </li>
          </ul>

          <div
            class="delete-button relative hover:cursor-pointer flex flex-row-reverse items-center justify-center
   mt-2 text-white text-sm font-medium px-12 py-4 gap-x-3 rounded-[20px] bg-[rgb(133,67,115)]
    hover:bg-[rgb(221,127,170)] shadow-lg transition-all duration-300 transform hover:scale-105 mb-3 "
            @click.stop.prevent="handleDeleteAllChat()"
          >
            <span> {{ t('deleteActions.deleteAll') }}</span>
            <TrashIcon class="h-6 w-6" />
          </div>
        </nav>
      </div>
    </div>

    <div class="lg:pl-80 relative">
      <OpenSidebarButton :show="!sidebarOpen"  @click="sidebarOpen = true" />
      <main class="min-h-screen bg-slate-100">
        <RouterView />
      </main>
    </div>
  </div>
</template>

<style scoped>
.masked-overflow {
  --scrollbar-width: 16px;

  --mask-height-top: 32px;
  --mask-height-bottom: 128px;

  overflow-y: auto;

  padding-bottom: calc(var(--mask-height-bottom) * 0.025);
  padding-right: 20px;

  --mask-image-content: linear-gradient(
    to bottom,
    black calc(100% - var(--mask-height-bottom)),
    transparent
  );
  --mask-size-content: calc(100% - var(--scrollbar-width)) 100%;
  /* --mask-image-scrollbar: linear-gradient(black, black); */
  --mask-size-scrollbar: var(--scrollbar-width) 100%;

  /* Mask */
  /*mask-image: var(--mask-image-content), var(--mask-image-scrollbar);*/
  mask-size: var(--mask-size-content), var(--mask-size-scrollbar);
  mask-position: 0 0,
  100% 0;
  mask-repeat: no-repeat, no-repeat;
}

.delete-button {
  background-color: rgb(133, 67, 115);
}

.delete-button:hover {
  background-color: rgb(221, 127, 170);
}
</style>